import { withRouter } from "react-router-dom";
import * as _ from "lodash";
import MainContent from "../../boxes/MainContent";
import PageHeader from "../../boxes/PageHeader";
import PageBody from "../../boxes/PageBody";
import LoadingScreen from "../../LoadingScreen";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import useCareBalance from '../hooks/useCareBalance';
import careBalanceService from '../../../services/careBalanceService';

const alafiaLogo = "/img/care-balance/alafia-on-light-logo.svg";
const infoCircleWhite = "/img/care-balance/info-circle-white.svg";

const commonButtonStyle = {
  flex: 1,
  paddingBlock: "16px",
  paddingInline: "24px",
  borderRadius: "128px",
  fontSize: "1.5rem",
  fontWeight: 600,
  lineHeight: "22px",
  letterSpacing: "0%",
};

export const MIN_FUND_CARE_BALANCE_AMOUNT = 1000;
export const MAX_FUND_CARE_BALANCE_AMOUNT = 2000000;

function FundCareBalanceContainer(props) {
  const { user, history } = props;

  const { t } = useTranslation("careBalance");

  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { wallet, loading } = useCareBalance({ hmoId: user?.hmoId });

  const formatNumber = useCallback((value) => {
    if (!value) return ""; // Handle empty or null input

    const cleanedValue = value.replace(/[^\d.]/g, "");
    const parts = cleanedValue.split(".");
    let integerPart = parts[0];
    let decimalPart = parts[1];

    const formattedIntegerPart = new Intl.NumberFormat("en-US").format(
      Number(integerPart)
    );

    if (decimalPart !== undefined) {
      decimalPart = decimalPart.substring(0, 2);
      return `${formattedIntegerPart}.${decimalPart}`;
    }

    return formattedIntegerPart;
  }, []);

  const validateAmount = useCallback((numericValue) => {
    if (!numericValue) {
      return "Amount is required.";
    }

    const num = Number(numericValue);

    if (isNaN(num)) {
      return "Invalid number entered."; // Should ideally be caught by regex but good to have
    }

    if (num < MIN_FUND_CARE_BALANCE_AMOUNT) {
      return `Cannot add less than ${new Intl.NumberFormat("en-US").format(
        MIN_FUND_CARE_BALANCE_AMOUNT
      )}.`;
    }

    if (num > MAX_FUND_CARE_BALANCE_AMOUNT) {
      return `Cannot add more than ${new Intl.NumberFormat("en-US").format(
        MAX_FUND_CARE_BALANCE_AMOUNT
      )}.`;
    }

    return "";
  }, []);

  const handleChange = useCallback(
    (e) => {
      let rawValue = e.target.value;

      const numericValue = rawValue.replace(/,/g, "");

      if (!/^\d*\.?\d*$/.test(numericValue)) {
        return;
      }

      setAmount(formatNumber(numericValue));
      const validationError = validateAmount(numericValue);
      setError(validationError);
    },
    [formatNumber, validateAmount]
  );

  const handleBlur = useCallback(() => {
    const numericAmount = amount.replace(/,/g, ""); // Get the clean numeric value
    const validationError = validateAmount(numericAmount);

    // Format to 2 decimal places on blur if it's a valid number
    if (numericAmount && !isNaN(Number(numericAmount)) && !validationError) {
      const num = Number(numericAmount);
      setAmount(
        new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(num)
      );
    } else if (numericAmount === "") {
      // If empty string on blur and no error, keep it empty
      setAmount("");
    }
  }, [amount, validateAmount]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const numericAmount = amount.replace(/,/g, "");
      const validationError = validateAmount(numericAmount);

      if (validationError) {
        setError(validationError);
      } else {
        setError("");
      }

      if (validationError) {
        return;
      }

      setIsSubmitting(true);

      const payload = {
        amount: +numericAmount,
        walletId: wallet.id,
        emailAddress: user?.emailAddress,
        postAuthorizationUrl: window.location.origin + "/fund-care-balance-success",
      };

      careBalanceService
        .fund(payload)
        .then(({ data }) => {
          window.location.href = data.authorizationUrl;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [amount, validateAmount]
  );

  if (loading || isSubmitting || _.isEmpty(user)) {
    return <LoadingScreen />;
  }

  return (
    <MainContent>
      <PageHeader title={t("fundWalletPageTitle")} />

      <PageBody>
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            rowGap: "32px",
          }}
        >
          <img src={alafiaLogo} alt="Alafia Logo" />

          <div
            style={{
              paddingBlock: "5.2rem",
              paddingInline: "3.2rem",
              backgroundColor: "#ffffff",
              maxWidth: "681px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              rowGap: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                backgroundColor: "#1171B0",
                paddingBlock: "8px",
                paddingInline: "16px",
                columnGap: "1.6rem",
                color: "#ffffff",
              }}
              data-testid="fund-care-balance-instructions"
            >
              <img src={infoCircleWhite} alt="Info Circle" />

              <ul
                style={{
                  listStyleType: "disc",
                  listStylePosition: "outside",
                  fontSize: "1.4rem",
                  fontWeight: 500,
                  letterSpacing: "0px",
                  lineHeight: "100%",
                }}
              >
                <li>- {t("fundWalletInstruction1")}</li>
                <li>- {t("fundWalletInstruction2")}</li>
              </ul>
            </div>

            <h3
              style={{
                textAlign: "center",
                color: "#4B4B4B",
                fontWeight: 600,
                fontSize: "2.4rem",
                lineHeight: "140%",
                letterSpacing: "0%",
                maxWidth: '436px',
                alignSelf:"center"
              }}
            >
              {t("fundWalletLabel")}
            </h3>

            <form
              style={{
                display: "flex",
                flexDirection: "column",
                rowGap: "16px",
              }}
              onSubmit={handleSubmit}
            >
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: error ? "1px solid #CB193B" : "1px solid #DDDEDF",
                    borderRadius: "8px",
                    overflow: "hidden",
                    paddingInline: "16px",
                  }}
                >
                  <div
                    style={{
                      color: "#1C1B1B",
                      fontSize: "1.4rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    NGN 
                    {/* <img src={caretDown} alt="Caret Down" /> */}
                  </div>
                  <input
                    type="text"
                    value={amount}
                    style={{
                      flexGrow: 1,
                      border: "none",
                      paddingBlock: "12px",
                      paddingInline: "12px",
                      fontSize: "1.4rem",
                      color: "#333",
                      outline: "none",
                    }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    data-testid="fund-care-balance-amount-input"
                  />
                </div>

                {error && (
                  <div
                    style={{
                      color: "#CB193B",
                      fontSize: "1.2rem",
                      lineHeight: "16px",
                      letterSpacing: "0%",
                      marginTop: "4px",
                    }}
                  >
                    {error}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  columnGap: "16px",
                }}
              >
                <button
                  type="button"
                  style={{
                    ...commonButtonStyle,
                    border: "1px solid #4F4F4F",
                    color: "#4F4F4F",
                  }}
                  onClick={() => history.goBack()}
                  data-testid="fund-care-balance-back-button"
                >
                  {t("fundWalletBackButtonLabel")}
                </button>

                <button
                  type="submit"
                  style={{
                    ...commonButtonStyle,
                    backgroundColor: "#094063",
                    color: "#ffffff",
                  }}
                  disabled={isSubmitting}
                  data-testid="fund-care-balance-proceed-button"
                >
                  {t("fundWalletProceedButtonLabel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </PageBody>
    </MainContent>
  );
}

export default withRouter(FundCareBalanceContainer);
